import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Type } from 'lucide-react';
import { Button } from './ui/button';
import { useTextSize, type TextSize } from '@/hooks/useTextSize';
import { cn } from '@/lib/utils';

/**
 * Text-size toggle in the header. Mirrors the LanguageSwitcher's UX:
 * icon-only ghost button, click opens a small menu with the size
 * options. Each option renders an "A" at its actual rendered size so
 * the visual is also the affordance — industry-standard pattern used
 * by BBC, NYT, gov.uk, Apple's accessibility surfaces, etc.
 */
type Option = {
  value: TextSize;
  /** Label shown in the menu. */
  label: string;
  /** Tailwind text size class used on the "A" preview. */
  preview: string;
};

const OPTIONS: Option[] = [
  { value: 'small', label: 'Small', preview: 'text-xs' },
  { value: 'normal', label: 'Default', preview: 'text-sm' },
  { value: 'large', label: 'Large', preview: 'text-base' },
  { value: 'x-large', label: 'Extra large', preview: 'text-lg' },
];

export const TextSizeSwitcher = () => {
  const { t } = useTranslation();
  const [size, setSize] = useTextSize();
  const [open, setOpen] = useState(false);
  const wrapper = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!wrapper.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const choose = (next: TextSize) => {
    setOpen(false);
    setSize(next);
  };

  return (
    <div ref={wrapper} className="relative">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('Text size')}
        title={t('Text size')}
      >
        <Type className="h-4 w-4" />
      </Button>
      {open && (
        <ul
          role="menu"
          className="absolute right-0 top-full z-50 mt-1 w-48 overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md"
        >
          {OPTIONS.map((opt) => {
            const active = opt.value === size;
            return (
              <li key={opt.value} role="none">
                <button
                  type="button"
                  role="menuitemradio"
                  aria-checked={active}
                  onClick={() => choose(opt.value)}
                  className={cn(
                    'flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-muted',
                    active && 'font-medium',
                  )}
                >
                  <span className="flex items-baseline gap-3">
                    <span className={cn('w-6 text-center font-semibold', opt.preview)}>A</span>
                    <span>{t(opt.label)}</span>
                  </span>
                  {active && <Check className="h-3.5 w-3.5 text-primary" />}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
